import React from "react";
import { Box, Button, IconButton, TextField, Typography } from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";

/**
 * Editor de la condición en modo ramas (0422 M5).
 *
 * Cada rama es { id, label, logic: "AND"|"OR", comparisons: [{ variable, operator, value }] }.
 * El `id` es el handle de salida que busca el SP: se genera una vez y NO cambia al renombrar la
 * rama, o las aristas ya dibujadas quedarían huérfanas. El orden importa: gana la primera rama
 * que se cumple, y si ninguna, sale por «Si no».
 */
export const OPERADORES = [
  { value: "equals", label: "Igual a" },
  { value: "notEquals", label: "Distinto de" },
  { value: "contains", label: "Contiene" },
  { value: "notContains", label: "No contiene" },
  { value: "startsWith", label: "Empieza por" },
  { value: "endsWith", label: "Termina en" },
  { value: "greater", label: "Mayor que" },
  { value: "less", label: "Menor que" },
  { value: "isNotEmpty", label: "Tiene valor" },
  { value: "isEmpty", label: "Está vacío" },
];

const SIN_VALOR = ["isNotEmpty", "isEmpty"];

export const nuevaRama = (n, base = {}) => ({
  id: `rama-${Date.now().toString(36)}-${n}`,
  label: `Rama ${n}`,
  logic: "AND",
  comparisons: [{ variable: base.variable || "", operator: base.operator || "equals", value: base.value || "" }],
});

const ConditionBranchesEditor = ({ branches, onChange }) => {
  const set = (i, cambios) => onChange(branches.map((b, k) => (k === i ? { ...b, ...cambios } : b)));
  const setComp = (i, j, cambios) =>
    set(i, { comparisons: branches[i].comparisons.map((c, k) => (k === j ? { ...c, ...cambios } : c)) });
  const mover = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= branches.length) return;
    const copia = [...branches];
    [copia[i], copia[j]] = [copia[j], copia[i]];
    onChange(copia);
  };

  return (
    <Box>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
        Se evalúan en orden y gana la primera que se cumple. Si ninguna, sale por «Si no».
      </Typography>

      {branches.map((b, i) => (
        <Box key={b.id} mb={2} p={1.5} border="1px solid rgba(255,152,0,0.5)" borderRadius={8}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <TextField
              size="small"
              label={`Rama ${i + 1}`}
              value={b.label || ""}
              onChange={(e) => set(i, { label: e.target.value })}
              variant="outlined"
              style={{ flex: 1 }}
            />
            <TextField
              size="small"
              select
              SelectProps={{ native: true }}
              value={b.logic || "AND"}
              onChange={(e) => set(i, { logic: e.target.value })}
              variant="outlined"
              style={{ width: 150 }}
            >
              <option value="AND">Todas (Y)</option>
              <option value="OR">Alguna (O)</option>
            </TextField>
            <Button size="small" onClick={() => mover(i, -1)} disabled={i === 0}>↑</Button>
            <Button size="small" onClick={() => mover(i, 1)} disabled={i === branches.length - 1}>↓</Button>
            <IconButton size="small" onClick={() => onChange(branches.filter((_, k) => k !== i))}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>

          {(b.comparisons || []).map((c, j) => (
            <Box key={j} display="flex" alignItems="center" mt={1} style={{ gap: 8 }}>
              <TextField
                size="small"
                placeholder="variable"
                value={c.variable || ""}
                onChange={(e) => setComp(i, j, { variable: e.target.value.trim() })}
                variant="outlined"
                style={{ flex: 1 }}
              />
              <TextField
                size="small"
                select
                SelectProps={{ native: true }}
                value={c.operator || "equals"}
                onChange={(e) => setComp(i, j, { operator: e.target.value })}
                variant="outlined"
                style={{ width: 140 }}
              >
                {OPERADORES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </TextField>
              <TextField
                size="small"
                placeholder="valor"
                value={c.value || ""}
                disabled={SIN_VALOR.includes(c.operator)}
                onChange={(e) => setComp(i, j, { value: e.target.value })}
                variant="outlined"
                style={{ flex: 1 }}
              />
              <IconButton
                size="small"
                disabled={(b.comparisons || []).length <= 1}
                onClick={() => set(i, { comparisons: b.comparisons.filter((_, k) => k !== j) })}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => set(i, { comparisons: [...(b.comparisons || []), { variable: "", operator: "equals", value: "" }] })}
            style={{ marginTop: 4 }}
          >
            Comparación
          </Button>
        </Box>
      ))}

      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => onChange([...branches, nuevaRama(branches.length + 1)])}
      >
        Añadir rama
      </Button>
    </Box>
  );
};

export default ConditionBranchesEditor;
